import { Component, OnInit } from '@angular/core';
import { EventsService } from '../../services';
import { ActivatedRoute } from '@angular/router';
import { EventAttendingEmployerViewData } from '../event-view/event-view.resolvers';
import { RegularFairDay, EventRegistrationFormConfigService } from '@csm/shared';
import * as _ from 'lodash';
import { Initials, ListItemElement, Monogram, StandardListPageModel, SkeletonLoaderModel } from '@symplicity/syng';

@Component({
    selector: 'cf-event-attending-employer-view',
    templateUrl: './event-attending-employer-view.html'
})
export class EventAttendingEmployerViewComponent implements OnInit {
    public readonly resolvedData = this.route.snapshot.data.resolvedData as EventAttendingEmployerViewData;
    public readonly eventId = this.resolvedData.eventId;
    public readonly eventData = this.resolvedData.eventData;
    public readonly isVirtualFair = this.resolvedData.isVirtualFair;

    public employerHeader: any;
    public registrationId: string;
    public employerData: any;
    public interested: boolean = false;
    public eventDays = [];
    public empProfileUrl: string;
    public empJobsUrl: string;
    public employerId: string;
    public weAreLookingForFormConfig: object;
    public otherFieldsConfig: object;
    public representativeInfoFormConfig: object;
    public eventRepData: Array<StandardListPageModel>;
    public readonly skeleton = new SkeletonLoaderModel({
        thumbnails: true,
        thumbnailsStyle: '',
        texts: 3
    });

    constructor(
        private eventsService: EventsService,
        private route: ActivatedRoute,
        private eventRegistrationFormConfigService: EventRegistrationFormConfigService,
        private initialsPipe: Initials
    ) {}

    ngOnInit() {
        this.registrationId = this.route.snapshot.paramMap.get('id');
        this.getEmployerData(this.eventId, this.registrationId);
    }

    getEmployerData(eventId: string, registrationId: string) {
        this.eventsService.getEmployerData(eventId, registrationId).subscribe(res => {
            this.employerData = res;
            this.employerId = res.employerId;
            this.setEmployerAddressText(this.employerData)

            this.employerHeader = {
                logo: this.employerData.logo,
                title: res.name,
                subtitles: this.addSubtitles(res)
            };

            this.setupForm(eventId);
            this.createAttendingDays();

            let empProfileBase = location.origin + '/students/app/employers/' + res.employerId;
            this.empProfileUrl = empProfileBase + '/overview';
            this.empJobsUrl = empProfileBase + '/jobs';
        })
    }

    addSubtitles(data) {
        let subtitles = [];

        if (data.employerIndustry) {
            let label = '';
            if (Array.isArray(data.employerIndustry)) {
                label = data.employerIndustry.join(', ');
            } else if (typeof data.employerIndustry === 'string') {
                label = data.employerIndustry;
            }
            if (label.length) {
                subtitles.push({
                    label: label,
                    styleClass: 'text-truncate'
                });
            }
        }

        if (data.day && Object.keys(data.day).length) {
            data.days = data.day;
            subtitles.push({
                label: this.eventsService.getEventRegistrationDatesLabel(data, this.eventData.status),
                styleClass: 'body-small secondary-text'
            });
        }
    
        return subtitles;
    }

    createAttendingDays() {
        const days = Array.isArray(this.employerData.day) ? this.employerData.day : _.isPlainObject(this.employerData.day) ? [ this.employerData.day ] : [];

        this.eventDays = days
            .sort((a, b) => a.daystart > b.daystart ? 1 : -1)
            .map(day => new RegularFairDay({
                id: day.eventday_id,
                start: day.daystart,
                end: day.dayend,
                isSingleDay: day.day_type === 'Single Day',
                date: day.displayStartDate,
                label: day.label,
                addressLabel: day.location.address && day.location.address.address_text ? day.location.address.address_text : '',
                mapUrl: day.location.locationAddressDisplayString ? `https://maps.google.com/?q=${day.location.locationAddressDisplayString.replace(/(?:\r\n|\r|\n)/g, ', ').replace(',,', ',')}` : null,
                address: day.location.locationAddressDisplayString || '',
                boothmapCount: day.boothmapCount,
                parkingInformation: day.parking_info || day.location.parking_info || ''
            }));
    }

    setupForm(eventId: string) {
        this.eventRegistrationFormConfigService.getFormConfigs(eventId, this.eventsService).subscribe((formStructure) => {
            const configs = this.eventRegistrationFormConfigService.setupFormConfigs(formStructure, this.employerData);
            if (configs.weAreLookingForFormConfig !== null) {
                this.weAreLookingForFormConfig = configs.weAreLookingForFormConfig;
            }

            if (configs.representativeInfoFormConfig !== null) {
                this.getEventRepData();
                this.representativeInfoFormConfig = configs.representativeInfoFormConfig;
            }

            if (configs.otherFieldsConfig !== null) {
                this.otherFieldsConfig = configs.otherFieldsConfig;
            }
        })
    }
    
    getEventRepData(): void {
        this.eventsService.getEventReps(this.eventId, this.registrationId).subscribe(res => {
            if (res) {
                this.eventRepData = this.getEvtRepsListItems(res.models);
            }
        });
    }

    getEvtRepsListItems(data: any): Array<StandardListPageModel> {
        const items: Array<StandardListPageModel> = new Array<StandardListPageModel>();
        data.map((contact: any) => {
            let titleAndDivision: string = this.getTitleAndDivision(contact);
            let item: StandardListPageModel = {
                id: contact.rep_id,
                texts: [
                    new ListItemElement({
                        label: contact.name,
                        defaultStyleType: 'subtitle'
                    })
                ],
                monogram: this.getMonogram(contact),
                rowAnchor: null,
                styleClass: 'no-borders no-padding space-top-md'
            };

            if (titleAndDivision) {
                item.texts.push(new ListItemElement({
                    label: titleAndDivision,
                    defaultStyleType: 'description',
                    styleClass: 'text-gray'
                }));
            }

            items.push(item);
        });
        return items;
    }

    getTitleAndDivision(contact: any): string {
        let titleAndDivision: string = '';

        if (contact.title) {
            titleAndDivision += contact.title;
        }

        if (contact.division) {
            if (titleAndDivision.length > 0) {
                titleAndDivision += ' - ';
            }

            titleAndDivision += contact.division;
        }

        return titleAndDivision;
    }

    getMonogram(contact: any): Monogram {
        let hasImage = (contact.image_small && contact.image_small._label);
        let monogram: Monogram = new Monogram({
            type: hasImage ? 'image' : 'label',
            isSquare: false,
            styleClass: hasImage ? 'avatar-md' : 'avatar-md monogram'
        });

        if (hasImage) {
            monogram.src = contact.image_small._label;
        } else {
            monogram.label = this.initialsPipe.transform(contact.name, null, null).toUpperCase();
        }

        return monogram;
    }

    private setEmployerAddressText(employerData) {
        let address = employerData.address;
        if (Array.isArray(address)) {
            address = address[0]
        }
        if (typeof address === 'object') {
            address = address.address_text
        }
        employerData.address = address
    }
}